import React from 'react';
import { useTranslation } from 'react-i18next';

import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Modal from '@mui/material/Modal';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import { alpha } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import Iconify from '../../../theme/iconify/iconify';

function NewCourse({ courseModal, createCourse, importCourse, setCourseModal, setOpen, setMessage, setSeverity }) {
    const [title, setTitle] = React.useState('');
    const fileInputRef = React.useRef(null);

    const { t } = useTranslation();

    const filePicker = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    }

    const handleFileChange = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        try {
            const text = await file.text();
            const course = JSON.parse(text);

            if (course.title && course.sections) importCourse(course);
            else {
                setMessage(t('courses.newCourse.import.error.invalid'));
                setSeverity('error');
                setOpen(true);
            }
        } catch (error) {
            console.error(error);
        }
    };

    React.useEffect(() => {
        if (!courseModal) {
            setTitle('');
        }
    }, [courseModal]);

    return (
        <Modal
            open={courseModal}
            onClose={() => setCourseModal(false)}
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
            }}
        >
            <Paper
                sx={{
                    padding: '24px',
                    minWidth: { xs: '95%', md: '40%' },
                    maxWidth: { xs: '95%', md: '40%' },
                }}
            >
                <Typography variant='h6' sx={{ mb: 2 }}>
                    {t('courses.newCourse.title')}
                </Typography>
                <Stack spacing={4}>
                    <TextField
                        id='mISLec_course_title'
                        label={t('courses.newCourse.input')}
                        variant='outlined'
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        fullWidth
                    />
                </Stack>
                <Stack direction='row' alignItems='center' sx={{ mt: { xs: 1, md: 2 } }}>
                    <Grid container spacing={1}>
                        <Grid item xs={12}>
                            <Grid
                                container
                                alignItems='start'
                                wrap='nowrap'
                                onClick={() => filePicker()}
                                sx={{
                                    display: 'inline-flex',
                                    cursor: 'pointer',
                                    p: 0.5,
                                    fontSize: '0.8rem',
                                    width: 'auto',
                                    '&:hover': {
                                        backgroundColor: 'rgba(145, 158, 171, 0.08)',
                                        borderRadius: '8px'
                                    }
                                }}
                            >
                                <Grid item sx={{ mr: 1, mt: 0.5, display: 'flex', alignItems: 'center' }}>
                                    <Iconify icon='eva:upload-fill' sx={{ color: 'primary.main' }} />
                                </Grid>
                                <Grid item>
                                    <Typography variant='body2' sx={{ fontWeight: '500' }}>
                                        {t('courses.newCourse.import.label')}
                                    </Typography>
                                    <Typography variant='caption' color='text.secondary'>
                                        {t('courses.newCourse.import.description')}
                                    </Typography>
                                </Grid>
                            </Grid>
                            <input
                                type='file'
                                accept='application/json'
                                ref={fileInputRef}
                                onChange={handleFileChange}
                                style={{ display: 'none' }}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Stack
                                direction='row'
                                alignItems='center'
                                mt={0.5}
                                sx={{
                                    justifyContent: {
                                        xs: 'space-between',
                                        md: 'flex-end'
                                    }
                                }}
                            >
                                <Button
                                    variant=''
                                    onClick={() => setCourseModal(false)}
                                    sx={{
                                        borderRadius: '8px !important',
                                        backgroundColor: (theme) => ({
                                            xs: alpha(theme.palette.error.main, 0.08),
                                            md: 'rgba(33, 43, 54, 0.08)'
                                        }),
                                        color: {
                                            xs: 'error.main',
                                            md: 'inherit'
                                        },
                                        '&:hover': {
                                            color: 'error.main',
                                            backgroundColor: (theme) => alpha(theme.palette.error.main, 0.08)
                                        }
                                    }}
                                >
                                    {t('courses.newCourse.cancel')}
                                </Button>
                                <Button
                                    variant=''
                                    onClick={() => createCourse(title)}
                                    sx={{
                                        ml: {
                                            xs: 0,
                                            md: 1
                                        },
                                        borderRadius: '8px !important',
                                        backgroundColor: (theme) => ({
                                            xs: alpha(theme.palette.primary.main, 0.08),
                                            md: 'rgba(33, 43, 54, 0.08)'
                                        }),
                                        color: {
                                            xs: 'primary.main',
                                            md: 'inherit'
                                        },
                                        '&:hover': {
                                            color: 'primary.main',
                                            backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.08)
                                        }
                                    }}
                                >
                                    {t('courses.newCourse.create')}
                                </Button>
                            </Stack>
                        </Grid>
                    </Grid>
                </Stack>
            </Paper>
        </Modal>
    );
}

export default NewCourse;